(() => {
  const config = window.QamarShippingProtectionConfig || {};
  const rawVariantId = config.variantId;
  const variantId = Number(rawVariantId);
  const variantIdString = rawVariantId ? String(rawVariantId) : '';
  const DISABLED_KEY = config.disabledKey || 'qamar_shipping_protection_disabled';

  if (!variantIdString) {
    console.warn('[ShippingProtection] Missing variant id. Set QamarShippingProtectionConfig.variantId.');
  }

  let isSyncing = false;

  // ── Cached shipping-protection price (cents) ──────────────────────
  let protectionPriceCents = 0;

  const resolveProtectionPrice = (cart) => {
    if (protectionPriceCents) return;

    if (cart && Array.isArray(cart.items)) {
      const item = cart.items.find(i => String(i.variant_id) === variantIdString);
      if (item) {
        protectionPriceCents = item.final_price || item.price || 0;
        return;
      }
    }

    const widget = document.querySelector('[data-protection-price-cents]');
    if (widget) {
      const cents = parseInt(widget.dataset.protectionPriceCents, 10);
      if (cents > 0) protectionPriceCents = cents;
    }
  };

  // ── Money formatter ────────────────────────────────────────────────
  const formatMoney = (cents) => {
    const format = window.theme?.settings?.money_with_currency_format
                || window.theme?.settings?.money_format
                || '${{amount}}';
    const amount      = (cents / 100).toFixed(2);
    const amountNoD   = Math.floor(cents / 100).toString();
    const amountWithC = amount.replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
    const amountNoDC  = amountNoD.replace(/(\d)(?=(\d{3})+$)/g, '$1,');

    return format
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.'))
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoD.replace(/(\d)(?=(\d{3})+$)/g, '$1.'))
      .replace('{{amount_with_apostrophe_separator}}', amount.replace(/(\d)(?=(\d{3})+\.)/g, "$1'"))
      .replace('{{amount_no_decimals}}', amountNoDC)
      .replace('{{amount}}', amountWithC);
  };

  // ── Cart counters ──────────────────────────────────────────────────
  const updateCartCounters = (cartState) => {
    const applyCount = (count) => {
      document.querySelectorAll('.cart-count-badge').forEach(badge => {
        if (count > 0) {
          badge.classList.remove('hidden');
          badge.textContent = count > 99 ? '+' : String(count);
        } else {
          badge.classList.add('hidden');
          badge.textContent = '';
        }
      });

      document.querySelectorAll('.cart__title-counter').forEach(counter => {
        counter.textContent = String(count);
      });

      document.querySelectorAll('.cart-drawer__title-counter').forEach(counter => {
        counter.textContent = String(count);
      });
    };

    if (cartState && Array.isArray(cartState.items)) {
      const visibleCount = cartState.items
        .filter(item => String(item.variant_id) !== variantIdString)
        .reduce((sum, item) => sum + item.quantity, 0);
      applyCount(visibleCount);
      return;
    }

    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const visibleCount = cart.items
          .filter(item => String(item.variant_id) !== variantIdString)
          .reduce((sum, item) => sum + item.quantity, 0);
        applyCount(visibleCount);
      })
      .catch(() => {});
  };

  // Force override the global updateCartCounters from global.js
  // so that cart updates correctly exclude the protection item.
  window.updateCartCounters = updateCartCounters;

  // ── Donation bar refresh ──────────────────────────────────────────
  // After any shipping-protection change, refresh the donation bar
  // with authoritative cart data (excluding protection variant).
  const refreshDonationBar = (cart) => {
    if (typeof window.updateDonationBar !== 'function') return;
    if (cart && Array.isArray(cart.items)) {
      window.updateDonationBar(cart);
    } else {
      window.updateDonationBar(); // triggers fetchAndUpdate fallback
    }
  };

  // ── Cart helpers & Interceptors ────────────────────────────────────
  // Override fetch globally to hide protection item from third-party apps,
  // and to inject it instantly on Add to Cart to avoid visual delays.
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] instanceof Request ? args[0].url : '');
    
    // Inject shipping protection on Add to Cart to prevent 1-second delay
    if (url && (url.includes('/cart/add'))) {
      if (!url.includes('unfiltered=true') && !isDisabled()) {
        try {
          const options = args[1] || {};
          if (options.method && options.method.toUpperCase() === 'POST' && typeof options.body === 'string') {
            const bodyObj = JSON.parse(options.body);
            // Convert simple {id, quantity} to items array format
            if (bodyObj.id && !bodyObj.items) {
              bodyObj.items = [{ id: bodyObj.id, quantity: bodyObj.quantity || 1 }];
              delete bodyObj.id;
              delete bodyObj.quantity;
            }
            if (bodyObj.items && Array.isArray(bodyObj.items)) {
              // Ensure we don't inject it if they are buying it directly (edge case)
              const hasProtection = bodyObj.items.some(i => String(i.id) === variantIdString);
              if (!hasProtection) {
                bodyObj.items.push({ id: variantIdString, quantity: 1 });
                args[1].body = JSON.stringify(bodyObj);
              }
            }
          }
        } catch (e) {} // Fallback to normal behavior if parsing fails
      }
    }

    const response = await originalFetch.apply(this, args);
    
    if (url && (url.includes('/cart.js') || url.includes('/cart/change') || url.includes('/cart/add'))) {
      if (url.includes('unfiltered=true')) return response;
      
      const clonedResponse = response.clone();
      try {
        const text = await clonedResponse.text();
        if (text) {
          const data = JSON.parse(text);
          if (data && data.items) {
            data.items = data.items.filter(item => String(item.variant_id) !== variantIdString);
            if (typeof data.item_count !== 'undefined') {
              data.item_count = data.items.reduce((total, item) => total + item.quantity, 0);
            }
            return new Response(JSON.stringify(data), {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers
            });
          }
        }
      } catch (e) {} // ignore
    }
    return response;
  };

  const originalXHR = window.XMLHttpRequest;
  window.XMLHttpRequest = function() {
    const xhr = new originalXHR();
    const originalOpen = xhr.open;
    xhr.open = function(method, url) {
      this._url = typeof url === 'string' ? url : '';
      return originalOpen.apply(this, arguments);
    };
    
    // We proxy responseText getter
    const originalResponseText = Object.getOwnPropertyDescriptor(originalXHR.prototype, 'responseText');
    if (originalResponseText) {
      Object.defineProperty(xhr, 'responseText', {
        get: function() {
          const text = originalResponseText.get.call(this);
          if (this._url && (this._url.includes('/cart.js') || this._url.includes('/cart/change') || this._url.includes('/cart/add'))) {
            if (!this._url.includes('unfiltered=true')) {
              try {
                const data = JSON.parse(text);
                if (data && data.items) {
                  data.items = data.items.filter(item => String(item.variant_id) !== variantIdString);
                  if (typeof data.item_count !== 'undefined') {
                    data.item_count = data.items.reduce((total, item) => total + item.quantity, 0);
                  }
                  return JSON.stringify(data);
                }
              } catch (e) {}
            }
          }
          return text;
        }
      });
    }
    return xhr;
  };

  const getCart = () => fetch('/cart.js?unfiltered=true').then(response => response.json());

  const getProtectionItems = (cart) =>
    cart.items.filter(item => String(item.variant_id) === variantIdString);

  const getRegularItems = (cart) =>
    cart.items.filter(item => String(item.variant_id) !== variantIdString);

  const isDisabled = () => sessionStorage.getItem(DISABLED_KEY) === 'true';

  const setDisabled = (value) => {
    if (value) {
      sessionStorage.setItem(DISABLED_KEY, 'true');
    } else {
      sessionStorage.removeItem(DISABLED_KEY);
    }
  };

  const postCart = (url, payload) => {
    const body = JSON.stringify({
      ...payload,
      sections_url: window.location.pathname
    });

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body
    }).then(response => response.json());
  };

  // ── Visual helpers (no API calls) ──────────────────────────────────
  const setWidgetState = (enabled) => {
    const widget = document.querySelector('[data-shipping-protection]');
    if (!widget) return;

    widget.classList.toggle('shipping-protection--disabled', !enabled);

    const toggle = widget.querySelector('[data-shipping-protection-toggle]');
    if (toggle) {
      toggle.checked = enabled;
    }
  };

  const setIconAppearance = (enabled) => {
    // Widget-level icon (summary area)
    const widget = document.querySelector('[data-shipping-protection]');
    if (widget) {
      const img = widget.querySelector('.shipping-protection__icon img');
      if (img) {
        img.style.filter = enabled ? '' : 'grayscale(1)';
        img.style.opacity = enabled ? '' : '0.6';
      }
    }

    // Line-item level icon (if visible)
    document.querySelectorAll('.cart-item--shipping-protection .shipping-protection__icon img').forEach(img => {
      img.style.filter = enabled ? '' : 'grayscale(1)';
      img.style.opacity = enabled ? '' : '0.6';
    });
  };

  const updateSubtotalFromServer = (totalPriceCents) => {
    const priceEl = document.querySelector('[data-cart-total-price]');
    if (!priceEl) return;
    priceEl.dataset.cartTotalPrice = String(totalPriceCents);
    priceEl.textContent = formatMoney(totalPriceCents);
  };

  // ── Optimistic state tracking ──────────────────────────────────────
  // We track the ABSOLUTE optimistic state so that rapid clicks don't
  // compound price deltas. Each click only adjusts the price if the
  // desired state differs from what the UI is currently showing.
  //   null  = unknown (initial, before first render)
  //   true  = UI shows protection enabled (price includes protection)
  //   false = UI shows protection disabled (price excludes protection)
  let optimisticState = null;

  /**
   * Apply optimistic visuals for the desired protection state.
   * Safe to call repeatedly — only adjusts price when state changes.
   */
  const applyOptimisticUI = (enabled) => {
    setWidgetState(enabled);
    setIconAppearance(enabled);

    // Only adjust price when state actually flips
    if (protectionPriceCents && optimisticState !== null && optimisticState !== enabled) {
      const priceEl = document.querySelector('[data-cart-total-price]');
      if (priceEl) {
        const currentCents = parseInt(priceEl.dataset.cartTotalPrice, 10) || 0;
        const newCents = Math.max(0, currentCents + (enabled ? protectionPriceCents : -protectionPriceCents));
        priceEl.dataset.cartTotalPrice = String(newCents);
        priceEl.textContent = formatMoney(newCents);
      }
    }

    optimisticState = enabled;
  };

  const initOptimisticState = () => {
    const toggle = document.querySelector('[data-shipping-protection-toggle]');
    if (toggle) {
      optimisticState = toggle.checked;
    }
  };

  // ── Serial toggle queue ────────────────────────────────────────────
  // Instead of a simple debounce, we use a serial queue pattern:
  //   1. Every click stores the desired state and resets a short timer.
  //   2. When the timer fires, we lock processing and execute.
  //   3. While `isUserToggling` is true, ALL syncs are blocked.
  //   4. `isProcessingQueue` prevents concurrent queue runs when a
  //      second timer fires while the first is still in async API calls.
  //   5. The running queue picks up any new pendingDesiredState on loop.
  let toggleDebounceTimer = null;
  let isUserToggling = false;       // true from first click until final API settles
  let isProcessingQueue = false;    // true while processToggleQueue is running (async)
  let pendingDesiredState = null;   // the latest desired state from user clicks

  const handleToggle = (event) => {
    const toggle = event.target.closest('[data-shipping-protection-toggle]');
    if (!toggle) return;

    const desiredEnabled = toggle.checked;

    // Apply optimistic UI INSTANTLY
    applyOptimisticUI(desiredEnabled);

    // Update the disabled flag
    setDisabled(!desiredEnabled);

    // Store the latest desired state
    pendingDesiredState = desiredEnabled;

    // Mark that the user is actively toggling (blocks all syncs)
    isUserToggling = true;

    // Reset the debounce timer — only fires after 300ms of quiet
    clearTimeout(toggleDebounceTimer);
    toggleDebounceTimer = setTimeout(() => {
      drainToggleQueue();
    }, 300);
  };

  /**
   * Starts processing the toggle queue, but only if not already running.
   * If already running, the active loop will pick up the new
   * pendingDesiredState automatically — no duplicate work needed.
   */
  const drainToggleQueue = async () => {
    if (isProcessingQueue) return; // already running — it will pick up the new state
    isProcessingQueue = true;

    try {
      while (pendingDesiredState !== null) {
        const desired = pendingDesiredState;
        pendingDesiredState = null; // consume it

        await executeToggle(desired);

        // If another click came in while we were processing, loop again
        if (pendingDesiredState !== null) {
          continue;
        }
        break;
      }
    } finally {
      isProcessingQueue = false;
      isUserToggling = false;
    }
  };

  /**
   * Executes a single add/remove operation and reconciles with server.
   */
  const executeToggle = async (desiredEnabled) => {
    try {
      const cart = await getCart();
      resolveProtectionPrice(cart);
      const protectionItems = getProtectionItems(cart);
      const isCurrentlyInCart = protectionItems.length > 0;

      let finalCart;

      if (desiredEnabled && !isCurrentlyInCart) {
        // Need to add protection
        const response = await postCart('/cart/add.js', {
          id: variantIdString,
          quantity: 1
        });

        if (!protectionPriceCents && response) {
          protectionPriceCents = response.final_price || response.price || 0;
        }

        finalCart = await getCart();

      } else if (!desiredEnabled && isCurrentlyInCart) {
        // Need to remove protection
        await postCart('/cart/change.js', {
          id: protectionItems[0].key,
          quantity: 0
        });
        finalCart = await getCart();

      } else {
        // Cart already matches desired state
        finalCart = cart;
      }

      // If another click came in while we were processing, do NOT update the UI.
      // Let the next loop iteration handle the UI update to avoid reverting the optimistic UI.
      if (pendingDesiredState !== null) {
        return;
      }

      // Reconcile everything from server
      if (finalCart && typeof finalCart.total_price !== 'undefined') {
        updateSubtotalFromServer(finalCart.total_price);
      }
      updateCartCounters(finalCart);
      refreshDonationBar(finalCart);
      optimisticState = desiredEnabled;

      // Enforce correct widget state
      setWidgetState(desiredEnabled);
      setIconAppearance(desiredEnabled);

    } catch (error) {
      console.error('[ShippingProtection] Toggle failed', error);
      // Revert optimistic UI on failure
      const revertState = !desiredEnabled;
      applyOptimisticUI(revertState);
      setDisabled(desiredEnabled);
    }
  };

  const handleDisableClick = (event) => {
    const button = event.target.closest('[data-shipping-protection-disable]');
    if (!button) return;

    event.preventDefault();

    // Route through the same toggle path
    applyOptimisticUI(false);
    setDisabled(true);

    pendingDesiredState = false;
    isUserToggling = true;

    clearTimeout(toggleDebounceTimer);
    toggleDebounceTimer = setTimeout(() => {
      drainToggleQueue();
    }, 300);
  };

  // ── Bare add / remove for sync (no optimistic UI) ──────────────────
  const addProtectionToCart = async () => {
    if (!variantIdString) return;
    setDisabled(false);

    try {
      const response = await postCart('/cart/add.js', {
        id: variantIdString,
        quantity: 1
      });

      if (!protectionPriceCents && response) {
        protectionPriceCents = response.final_price || response.price || 0;
      }

      // Reconcile price
      const finalCart = await getCart();
      if (finalCart && typeof finalCart.total_price !== 'undefined') {
        updateSubtotalFromServer(finalCart.total_price);
      }
      updateCartCounters(finalCart);
      refreshDonationBar(finalCart);
      setWidgetState(true);
      setIconAppearance(true);
      optimisticState = true;
    } catch (error) {
      console.error('[ShippingProtection] Add failed', error);
    }
  };

  const removeProtectionFromCart = async (lineKey) => {
    if (!lineKey) return;

    try {
      await postCart('/cart/change.js', {
        id: lineKey,
        quantity: 0
      });

      const finalCart = await getCart();
      if (finalCart && typeof finalCart.total_price !== 'undefined') {
        updateSubtotalFromServer(finalCart.total_price);
      }
      updateCartCounters(finalCart);
      refreshDonationBar(finalCart);
      setWidgetState(false);
      setIconAppearance(false);
      optimisticState = false;
    } catch (error) {
      console.error('[ShippingProtection] Remove failed', error);
    }
  };

  const enforceSingleProtection = async (protectionItems) => {
    if (!protectionItems.length) return;

    const [keep, ...extras] = protectionItems;
    const tasks = [];

    if (keep.quantity > 1) {
      tasks.push(postCart('/cart/change.js', { id: keep.key, quantity: 1 }));
    }

    extras.forEach(item => {
      tasks.push(postCart('/cart/change.js', { id: item.key, quantity: 0 }));
    });

    if (tasks.length) {
      await Promise.all(tasks);
      const finalCart = await getCart();
      if (finalCart && typeof finalCart.total_price !== 'undefined') {
        updateSubtotalFromServer(finalCart.total_price);
      }
      updateCartCounters(finalCart);
      refreshDonationBar(finalCart);
    }
  };

  const resetDisabledStateIfCartEmpty = async (cart) => {
    const regularItems = getRegularItems(cart);
    if (regularItems.length > 0) return;

    const protectionItems = getProtectionItems(cart);
    setDisabled(false);

    if (protectionItems.length) {
      const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        // Use cart.js to remove it so it automatically fetches and renders the Empty Cart UI
        cartItems.updateQuantity(protectionItems[0].key, 0);
      } else {
        await removeProtectionFromCart(protectionItems[0].key);
        window.location.reload();
      }
    }
  };

  // ── Sync logic ─────────────────────────────────────────────────────
  // COMPLETELY blocked while user is actively toggling (from first
  // click through final API settlement). This prevents sync from
  // undoing the user's toggle.
  const syncShippingProtection = async () => {
    if (isSyncing || !variantIdString || isUserToggling) return;

    // Instantly enforce the visual state based on sessionStorage (prevents fake "ON" for disabled users)
    const currentlyDisabled = isDisabled();
    setWidgetState(!currentlyDisabled);
    setIconAppearance(!currentlyDisabled);
    optimisticState = !currentlyDisabled;

    isSyncing = true;

    try {
      const cart = await getCart();
      resolveProtectionPrice(cart);
      updateCartCounters(cart);

      const protectionItems = getProtectionItems(cart);
      const regularItems = getRegularItems(cart);

      if (regularItems.length === 0) {
        await resetDisabledStateIfCartEmpty(cart);
        return;
      }

      if (isDisabled() && protectionItems.length) {
        await removeProtectionFromCart(protectionItems[0].key);
        return;
      }

      if (protectionItems.length > 1 || (protectionItems[0] && protectionItems[0].quantity > 1)) {
        await enforceSingleProtection(protectionItems);
        return;
      }

      if (!protectionItems.length && !isDisabled()) {
        await addProtectionToCart();
      }
    } catch (error) {
      console.error('[ShippingProtection] Sync failed', error);
    } finally {
      isSyncing = false;
    }
  };

  // ── Event listeners ────────────────────────────────────────────────
  document.addEventListener('change', handleToggle);
  document.addEventListener('click', handleDisableClick);

  document.addEventListener('DOMContentLoaded', () => {
    resolveProtectionPrice(null);
    initOptimisticState();
    syncShippingProtection();
  });

  [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'product:added',
    'ajaxProduct:added',
    'cart-drawer:refresh',
    'theme:cart:change'
  ].forEach(eventName => {
    document.addEventListener(eventName, syncShippingProtection);
  });

  window.QamarShippingProtection = {
    sync: syncShippingProtection,
    disable: () => {
      applyOptimisticUI(false);
      setDisabled(true);
      pendingDesiredState = false;
      isUserToggling = true;
      clearTimeout(toggleDebounceTimer);
      drainToggleQueue();
    },
    add: () => {
      applyOptimisticUI(true);
      setDisabled(false);
      pendingDesiredState = true;
      isUserToggling = true;
      clearTimeout(toggleDebounceTimer);
      drainToggleQueue();
    },
    remove: removeProtectionFromCart,
    getCart
  };
})();
